import React from 'react'
import styled from 'styled-components'
import SunEditor from 'suneditor-react'
import SunEditorCore from 'suneditor/src/lib/core'
import 'suneditor/dist/css/suneditor.min.css'
import { editorStyles, theme } from 'config'
import { rootStore } from 'app'
import { UserType } from 'types'

interface RichTextEditorProps {
  value?: string
  defaultValue?: string
  readOnly?: boolean
  disable?: boolean
  autoFocus?: boolean
  resizeEnable?: boolean
  transparent?: boolean
  hideToolbar?: boolean
  isEditor?: boolean
  onChange?: (content: string) => void
  onDiscard?: () => string | null
  onFocus?: (event: FocusEvent) => void
  onHide?: () => void
  onSave?: (contents: string) => void
  onBlur?: (event: FocusEvent, editorContents: string) => void
  onImageUpload?: (
    targetImgElement: HTMLImageElement,
    index: number,
    state: string,
    imageInfo: object,
    remainingFilesCount: number
  ) => void
  onImageUploadBefore?: (
    files: Array<File>,
    info: object,
    uploadHandler: Function
  ) => void
  onImageUploadError?: (errorMessage: string, result: any) => void
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  autoFocus,
  defaultValue,
  onFocus,
  onChange,
  disable,
  readOnly,
  onHide,
  value,
  transparent,
  onSave,
  onImageUpload,
  onImageUploadBefore,
  ...props
}) => {
  const [hideToolbar, setHideToolbar] = React.useState<boolean>(true)
  const isEditor = rootStore.authStore.getCurUserType === UserType.Editor
  const containerRef = React.useRef<HTMLDivElement>(null)
  const editor = React.useRef<SunEditorCore>()
  const getSunEditorInstance = (sunEditor: SunEditorCore) => {
    editor.current = sunEditor
  }

  return (
    <StyleOverrides
      transparent={transparent}
      disable={disable}
      hideToolbar={hideToolbar}
      $isEditor={isEditor}
      ref={containerRef}
    >
      <SunEditor
        getSunEditorInstance={getSunEditorInstance}
        setDefaultStyle={`font-family: ${theme.typography.fontFamily}`}
        setOptions={{
          fontSizeUnit: 'pt',
          fontSize: [18, 20, 22],
          formats: ['h1', 'h2', 'h3', 'h4', 'h5', 'p'],
          buttonList: [
            ['formatBlock', 'bold', 'underline', 'italic', 'blockquote'],
            [
              '-right',
              'fontSize',
              'fontColor',
              'hiliteColor',
              'align',
              'horizontalRule',
              'list',
              'link',
              'image',
              'video',
            ],
          ],
        }}
        autoFocus={autoFocus}
        height='100%'
        width='100%'
        resizeEnable='false'
        onChange={onChange}
        onSave={onSave}
        onBlur={(e) => {
          setTimeout(() => {
            if (!containerRef.current?.contains(document.activeElement))
              setHideToolbar(true)
          }, 1)
        }}
        readOnly={readOnly}
        disable={disable}
        setContents={value}
        defaultValue={defaultValue}
        hideToolbar={hideToolbar}
        onImageUpload={onImageUpload}
        onImageUploadBefore={onImageUploadBefore}
        onFocus={(event) => {
          setHideToolbar(false)
          onFocus && onFocus(event)
        }}
        {...props}
      />
    </StyleOverrides>
  )
}

const TOOLBAR_HEIGHT = '5.5rem'

const StyleOverrides = styled.div<{
  transparent: boolean
  disable: boolean
  hideToolbar: boolean
  $isEditor?: boolean
}>`
  ${editorStyles}
  max-width: 70rem;
  width: 100%;

  .se-wrapper {
    border: 1px solid transparent;
    transition: border 0.2s ease;
    height: 100%;

    &:hover,
    &:focus {
      border: ${(p) =>
        p.disable ? '1px solid transparent' : '1px solid rgba(0, 0, 0, 0.1)'};
    }
  }

  .se-btn-module-border {
    border: none;
  }

  .sun-editor {
    background-color: ${(p) => (p.transparent ? 'transparent' : 'white')};
    border: none;
    height: 100%;

    .se-toolbar {
      display: ${(p) => (p.hideToolbar ? 'none' : undefined)};
      transition: opacity 0.2s ease 0s, visibility 0s linear 0.2s;
      position: absolute;
      top: -${TOOLBAR_HEIGHT};
      background: ${theme.palette.black.main};
      z-index: 1100;
      outline: none;
      border-radius: 0.4rem;

      .se-btn-list svg {
        color: ${theme.palette.black.main};
      }

      &::after {
        content: '';
        position: absolute;
        left: 0;
        bottom: -1rem;
        height: 1rem;
        width: 100%;
      }
    }

    .se-btn {
      &:enabled:active,
      &:enabled:focus,
      &:enabled:hover {
        box-shadow: none;
      }

      &:enabled:focus,
      &:enabled:active {
        background-color: rgba(255, 255, 255, 0.1);
      }

      &:enabled.active {
        color: #4592ff;
      }

      &:enabled.on,
      &:enabled.on:focus,
      &:enabled.on:hover {
        background-color: rgba(255, 255, 255, 0.05);
        border-color: transparent;
      }
    }

    .sun-editor-editable {
      background-color: transparent;
    }

    .se-menu-list {
      .se-btn {
        color: ${theme.palette.white.main};
        transition: all 0.2s ease;

        &:hover {
          background-color: rgba(255, 255, 255, 0.05);
        }
      }
    }

    ${({ $isEditor }) =>
      !$isEditor &&
      `
    .se-controller.se-controller-resizing {
      display: none !important;
      pointer-events: none;
    }

    .se-line-breaker-component {
      display: none !important;
    }

    .se-modal-resize {
      display: none !important;
      pointer-events: none;
    }

    .se-resizing-container {
      display: none !important;
      pointer-events: none;
      border: 1px solid red;
    }

    .se-resizing-bar {
      display: none !important;
    }
  `}
`
